/**
 * Decisions the grid needs before it can be logged, above it:
 * - which Redmine project takes "Resto de jornada" or an opportunity that is
 *   not linked to Redmine (stored on the server, so it survives a reload and
 *   the hours show up on the line they were logged from);
 * - the activity of each line;
 * - lines the person cannot log on, and why.
 * The suggested project is only offered as a button: nothing is chosen until
 * the person clicks.
 */
import { useMemo, useState } from 'react'
import styled from 'styled-components'
import type { ImputacionLine, ImputacionMonth, ImputacionProjectGroup, ImputacionProjectOption } from '../../../types/imputacion'
import { fmtH, orderedLines } from './imputacionLogic'

export interface LineSettingsStripProps {
    month: ImputacionMonth
    isAdmin: boolean
    /** Saves a row choice and reloads the month. Rejects with a message to show. */
    onChoose: (rowKey: string, projectId: number | null, activityId: number | null) => Promise<void>
    /** A submit is running: a choice now would re-read the month under it. */
    disabled?: boolean
}

const Strip = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`

const Row = styled.div<{ $tone?: 'warn' | 'muted' | 'ok' }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  border-left: 4px solid
    ${({ $tone }) => ($tone === 'warn' ? '#FF9800' : $tone === 'ok' ? '#4CAF50' : '#BDBDBD')};
  background: ${({ $tone }) => ($tone === 'warn' ? '#FFF8E1' : $tone === 'ok' ? '#F1F8E9' : '#F7F7F7')};
`

const LinkButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  color: #1565C0;
  text-decoration: underline;
  cursor: pointer;
  font-size: inherit;
`

const Pill = styled.button`
  border: 1px solid ${({ theme }) => theme.colors.primary};
  background: white;
  color: ${({ theme }) => theme.colors.primary};
  border-radius: 14px;
  padding: 3px 10px;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const PickerBox = styled.div`
  flex-basis: 100%;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: white;
  padding: 8px;
  max-height: 260px;
  overflow-y: auto;
`

const Search = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 6px 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  margin-bottom: 6px;
`

const GroupTitle = styled.div`
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textMuted};
  margin: 6px 0 2px;
`

const Option = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  border: none;
  background: none;
  padding: 4px 6px;
  border-radius: 4px;
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontSizes.sm};

  &:hover,
  &:focus {
    background: ${({ theme }) => theme.colors.secondary};
  }
`

const ActivitySelect = styled.select`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 12px;
  padding: 2px 8px;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  background: white;
`

const Hint = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
`

const ErrorText = styled.span`
  color: #C62828;
  font-size: ${({ theme }) => theme.fontSizes.xs};
`

const GROUPS: { key: ImputacionProjectGroup; title: string }[] = [
    { key: 'plan', title: 'Proyectos de tu plan' },
    { key: 'recent', title: 'Recientes' },
    { key: 'other', title: 'Tus proyectos' },
]

/** Row-choice key of a line that takes its project from a choice. */
export function rowKeyOf(line: ImputacionLine): string | null {
    if (line.choice_key) return line.choice_key
    const src = line.sources[0]
    if (!src) return null
    if (src.is_default) return 'default'
    return src.opportunity_id !== null ? `opp:${src.opportunity_id}` : null
}

/** "Resto de jornada" or the opportunity's name, for the row the choice belongs to. */
function choiceLabel(line: ImputacionLine, rowKey: string): string {
    if (rowKey === 'default') return 'Resto de jornada'
    const oppId = Number(rowKey.slice(4))
    return line.sources.find((s) => s.opportunity_id === oppId)?.label ?? line.label
}

function ProjectPicker({
    projects,
    onPick,
    disabled,
}: {
    projects: ImputacionProjectOption[]
    onPick: (p: ImputacionProjectOption) => void
    disabled: boolean
}) {
    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState('')
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase()
        return q ? projects.filter((p) => p.name.toLowerCase().includes(q) || String(p.id) === q) : projects
    }, [projects, query])

    return (
        <>
            <Pill type="button" onClick={() => setOpen((v) => !v)} disabled={disabled} aria-expanded={open}>
                buscar proyecto ▾
            </Pill>
            {open && (
                <PickerBox role="listbox" aria-label="Proyectos de Redmine">
                    <Search
                        autoFocus
                        aria-label="Buscar proyecto"
                        placeholder="Buscar proyecto…"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                    />
                    {filtered.length === 0 && <Hint>No hay proyectos que coincidan.</Hint>}
                    {GROUPS.map((g) => {
                        const items = filtered.filter((p) => p.group === g.key)
                        if (!items.length) return null
                        return (
                            <div key={g.key}>
                                <GroupTitle>{g.title}</GroupTitle>
                                {items.map((p) => (
                                    <Option
                                        key={p.id}
                                        type="button"
                                        role="option"
                                        aria-selected={false}
                                        onClick={() => {
                                            setOpen(false)
                                            onPick(p)
                                        }}
                                    >
                                        {p.name}
                                    </Option>
                                ))}
                            </div>
                        )
                    })}
                </PickerBox>
            )}
        </>
    )
}

export function LineSettingsStrip({ month, isAdmin, onChoose, disabled = false }: LineSettingsStripProps) {
    const [busy, setBusy] = useState<string | null>(null)
    const [errors, setErrors] = useState<Record<string, string>>({})
    const [changing, setChanging] = useState<string | null>(null)
    const lines = orderedLines(month)
    const editable = month.can_edit && !disabled

    const choose = async (rowKey: string, projectId: number | null, activityId: number | null) => {
        setBusy(rowKey)
        setErrors((e) => ({ ...e, [rowKey]: '' }))
        try {
            await onChoose(rowKey, projectId, activityId)
            setChanging(null)
        } catch (err) {
            setErrors((e) => ({ ...e, [rowKey]: err instanceof Error ? err.message : 'No se pudo guardar la elección' }))
        } finally {
            setBusy(null)
        }
    }

    const needsChoice = lines.filter((l) => l.kind === 'needs_choice')
    const chosen = lines.filter((l) => l.kind !== 'needs_choice' && l.choice_key)
    const blocked = lines.filter((l) => l.kind !== 'needs_choice' && l.kind !== 'hidden' && !l.loggable)
    const withActivity = lines.filter((l) => l.loggable && l.redmine_project_id !== null && l.activities.length > 0)
    const defaultPending = needsChoice.some((l) => l.sources.some((s) => s.is_default))

    if (!needsChoice.length && !chosen.length && !blocked.length && !(month.can_edit && withActivity.length)) return null

    const pickerRow = (line: ImputacionLine, rowKey: string) => (
        <>
            {line.suggested_project && (
                <Pill
                    type="button"
                    disabled={!editable || busy !== null}
                    onClick={() => choose(rowKey, line.suggested_project!.id, null)}
                >
                    Usar «{line.suggested_project.name}»
                </Pill>
            )}
            <ProjectPicker
                projects={month.projects}
                disabled={!editable || busy !== null}
                onPick={(p) => choose(rowKey, p.id, null)}
            />
        </>
    )

    return (
        <Strip>
            {needsChoice.map((line) => {
                const rowKey = rowKeyOf(line)
                if (!rowKey) return null
                const isDefault = rowKey === 'default'
                return (
                    <Row key={line.key} $tone="warn">
                        <span>
                            {isDefault
                                ? `«Resto de jornada» (${fmtH(line.summary.plan_month)} este mes): ¿en qué proyecto lo imputas?`
                                : `«${choiceLabel(line, rowKey)}» no está vinculada a Redmine: ¿en qué proyecto lo imputas?`}
                        </span>
                        {pickerRow(line, rowKey)}
                        {busy === rowKey && <Hint>Recarga para imputar en el nuevo proyecto…</Hint>}
                        {errors[rowKey] && <ErrorText role="alert">{errors[rowKey]}</ErrorText>}
                    </Row>
                )
            })}
            {isAdmin && defaultPending && (
                <Hint>Consejo: configura DEFAULT_BILLING_PROJECT_CODE para que nadie tenga que elegir.</Hint>
            )}
            {chosen.map((line) => {
                const rowKey = line.choice_key as string
                return (
                    <Row key={line.key} $tone="ok">
                        <span>
                            {choiceLabel(line, rowKey)} → {line.redmine_project_name ?? line.label} ✓
                        </span>
                        {editable && changing !== rowKey && (
                            <LinkButton type="button" onClick={() => setChanging(rowKey)}>
                                (cambiar)
                            </LinkButton>
                        )}
                        {editable && changing === rowKey && pickerRow(line, rowKey)}
                        {busy === rowKey && <Hint>Recarga para imputar en el nuevo proyecto…</Hint>}
                        {errors[rowKey] && <ErrorText role="alert">{errors[rowKey]}</ErrorText>}
                    </Row>
                )
            })}
            {blocked.map((line) => (
                <Row key={line.key} $tone="muted">
                    <span>
                        {line.label} — {line.not_loggable_reason ?? 'No se puede imputar en este proyecto.'} No se incluirá al
                        completar.
                    </span>
                </Row>
            ))}
            {month.can_edit && withActivity.length > 0 && (
                <Row $tone="muted">
                    <span>Actividad:</span>
                    {withActivity.map((line) => {
                        const rowKey = `proj:${line.redmine_project_id}`
                        return (
                            <label key={line.key} style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                                <span>{line.label}</span>
                                <ActivitySelect
                                    aria-label={`Actividad de ${line.label}`}
                                    value={line.activity_id ?? ''}
                                    disabled={busy !== null || disabled}
                                    onChange={(e) => choose(rowKey, null, e.target.value ? Number(e.target.value) : null)}
                                >
                                    {line.activity_id === null && <option value="">Elegir actividad ▾</option>}
                                    {line.activities.map((a) => (
                                        <option key={a.id} value={a.id}>
                                            {a.name}
                                        </option>
                                    ))}
                                </ActivitySelect>
                                {errors[rowKey] && <ErrorText role="alert">{errors[rowKey]}</ErrorText>}
                            </label>
                        )
                    })}
                </Row>
            )}
        </Strip>
    )
}
